// Draft form helpers for the acceptance page. Plain module with no Vue and no imports, so it runs
// under `node --test`.
//
// The page edits a flat form keyed by the API's paths ("arrival.date", "checklist.plates.answer").
// It compares it with the last saved values and sends only the changed fields as PATCH `set`.

export const FORM_PATHS = [
  "arrival.date",
  "arrival.mode",
  "arrival.transporterName",
  "arrival.incomingFir",
  "identity.vinRead",
  "identity.plate",
  "identity.plateCountry",
  "identity.vehicleClass",
  "owner.name",
  "owner.taxCode",
  "deliverer.name",
  "deliverer.idDocument.type",
  "deliverer.idDocument.number",
  "authorityToDeliver.basis",
  "authorityToDeliver.note",
  "hazards.leaks",
  "hazards.fireOrFlood",
  "hazards.battery",
  "hazards.missingParts",
  "hazards.notes",
  "quarantine.decision",
];

// Saved exactly as entered by the API; every other text is trimmed there.
const UNTRIMMED = new Set(["identity.vinRead"]);

function readPath(obj, path) {
  return path
    .split(".")
    .reduce((value, key) => (value == null ? undefined : value[key]), obj);
}

export function normalise(path, value) {
  if (value === undefined || value === null) return null;
  if (typeof value !== "string") return value;
  if (!value.trim()) return null;
  return UNTRIMMED.has(path) ? value : value.trim();
}

// The flat form for a dossier from GET / PATCH.
export function formFromDossier(dossier) {
  const form = {};
  FORM_PATHS.forEach((path) => {
    const value = readPath(dossier, path);
    form[path] = value === undefined ? null : value;
  });
  ((dossier && dossier.checklist) || []).forEach((line) => {
    form[`checklist.${line.code}.answer`] = line.answer || null;
    form[`checklist.${line.code}.reason`] = line.reason || null;
  });
  return form;
}

export function dirtyPaths(form, saved) {
  return Object.keys(form).filter(
    (path) => normalise(path, form[path]) !== normalise(path, saved[path]),
  );
}

// Nested `set` with only the changed fields. A checklist line always sends answer and reason
// together, because the API checks the reason against the answer.
export function buildSet(form, saved) {
  const set = {};
  const assign = (path) => {
    const keys = path.split(".");
    let node = set;
    keys.slice(0, -1).forEach((key) => {
      if (!node[key]) node[key] = {};
      node = node[key];
    });
    node[keys[keys.length - 1]] = normalise(path, form[path]);
  };
  dirtyPaths(form, saved).forEach((path) => {
    const line = /^checklist\.([^.]+)\.(answer|reason)$/.exec(path);
    if (line) {
      assign(`checklist.${line[1]}.answer`);
      assign(`checklist.${line[1]}.reason`);
    } else {
      assign(path);
    }
  });
  return set;
}

// After a save: take the server's values, except where the user typed again while it ran.
export function mergeAfterSave(form, sent, server) {
  const merged = { ...server };
  Object.keys(form).forEach((path) => {
    const typedSince =
      normalise(path, form[path]) !== normalise(path, sent[path]);
    if (typedSince) merged[path] = form[path];
  });
  return merged;
}

// Same normalisation as the API (api/core/compliance/identity.js), for the live preview only.
export function normaliseVin(value) {
  if (typeof value !== "string") return null;
  const normalised = value.toUpperCase().replace(/[\s.\-_/]/g, "");
  return normalised || null;
}

// Today's Europe/Rome civil date as YYYY-MM-DD (the API rejects later arrival dates).
export function todayRome(now = new Date()) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Europe/Rome",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const get = (type) => parts.find((part) => part.type === type).value;
  return `${get("year")}-${get("month")}-${get("day")}`;
}
